type Color = {
    type: string;
    values: (number | string)[];
    colorSpace?: string;
};

/**
 * Returns a number whose value is limited to the given range.
 * @param {number} value The value to be clamped
 * @param {number} min The lower boundary of the output range
 * @param {number} max The upper boundary of the output range
 * @returns {number} A number in the range [min, max]
 */
function clamp(value: number, min = 0, max = 1): number {
    return Math.min(Math.max(min, value), max);
}

/**
 * Converts a color from CSS hex format to CSS rgb format.
 * @param {string} color - Hex color, i.e. #nnn or #nnnnnn
 * @returns {string} A CSS rgb color string
 */
export function hexToRgb(color: string): string {
    const colorSub = color.substr(1);

    const re = new RegExp(`.{1,${colorSub.length >= 6 ? 2 : 1}}`, 'g');
    let colors = colorSub.match(re);

    if (colors && colors[0].length === 1) {
        colors = colors.map((n) => n + n);
    }

    return colors
        ? `rgb${colors.length === 4 ? 'a' : ''}(${colors
              .map((n, index) => {
                  return index < 3 ? parseInt(n, 16) : Math.round((parseInt(n, 16) / 255) * 1000) / 1000;
              })
              .join(', ')})`
        : '';
}

/**
 * Returns an object with the type and values of a color.
 *
 * Note: Does not support rgb % values.
 * @param {string} color - CSS color, i.e. one of: #nnn, #nnnnnn, rgb(), rgba(), hsl(), hsla()
 * @returns {object} - A color object: {type: string, values: number[]}
 */
export function decomposeColor(color: string): Color {
    if (color.charAt(0) === '#') {
        return decomposeColor(hexToRgb(color));
    }

    const marker = color.indexOf('(');
    const type = color.substring(0, marker);

    if (['rgb', 'rgba', 'hsl', 'hsla', 'color'].indexOf(type) === -1) {
        throw new Error(
            `Honeycomb-UI: '${color}' is an unsupported color format. The supported formats are #nnn, #nnnnnn, rgb(), rgba(), hsl(), hsla(), color().`
        );
    }

    let values: string | string[] | number[] = color.substring(marker + 1, color.length - 1);
    let colorSpace: string | undefined;

    if (type === 'color') {
        values = values.split(' ');
        colorSpace = values.shift() || '';
        if (values.length === 4 && values[3].charAt(0) === '/') {
            values[3] = values[3].substr(1);
        }
        if (['rgb', 'rgba', 'hsl', 'hsla', 'color'].indexOf(type) === -1) {
            throw new Error(
                `Honeycomb-UI: '${colorSpace}' is an unsupported color space. The supported color spaces are srgb, display-p3, a98-rgb, prophoto-rgb, rec-2020.`
            );
        }
    } else {
        values = values.split(',');
    }
    values = values.map((value) => parseFloat(value));

    return { type, values, colorSpace };
}

/**
 * Converts a color object with type and values to a string.
 * @param {object} backgroundColor - Decomposed color
 * @param {string} backgroundColor.type - One of: 'rgb', 'rgba', 'hsl', 'hsla'
 * @param {array} backgroundColor.values - [n,n,n] or [n,n,n,n]
 * @returns {string} A CSS color string
 */
export function recomposeColor(color: Color): string {
    const { type, colorSpace } = color;
    let { values } = color;

    if (type.indexOf('rgb') !== -1) {
        // Only convert the first 3 values to int (i.e. not alpha)
        // eslint-disable-next-line
        values = values.map((n, i) => (i < 3 ? parseInt(n.toString(), 10) : n));
    } else if (type.indexOf('hsl') !== -1) {
        values[1] = `${values[1]}%`;
        values[2] = `${values[2]}%`;
    }
    let value: string;
    if (type.indexOf('color') !== -1) {
        value = `${colorSpace} ${values.join(' ')}`;
    } else {
        value = `${values.join(', ')}`;
    }

    return `${type}(${value})`;
}

/**
 * Converts a color from hsl format to rgb format.
 * @param {string} color - HSL color values
 * @returns {string} rgb color values
 */
export function hslToRgb(color: string): string {
    const decomposedColor = decomposeColor(color);
    const { values } = decomposedColor;
    const h = values[0] as number;
    const s = (values[1] as number) / 100;
    const l = (values[2] as number) / 100;
    const a = s * Math.min(l, 1 - l);
    const f = (n: number, k = (n + h / 30) % 12): number => l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);

    let type = 'rgb';
    const rgb = [Math.round(f(0) * 255), Math.round(f(8) * 255), Math.round(f(4) * 255)];

    if (decomposedColor.type === 'hsla') {
        type += 'a';
        rgb.push(values[3] as number);
    }

    return recomposeColor({ type, values: rgb });
}

/**
 * The relative brightness of any point in a color space,
 * normalized to 0 for darkest black and 1 for lightest white.
 *
 * Formula: https://www.w3.org/TR/WCAG20-TECHS/G17.html#G17-tests
 * @param {string} color - CSS color, i.e. one of: #nnn, #nnnnnn, rgb(), rgba(), hsl(), hsla(), color()
 * @returns {number} The relative brightness of the color in the range 0 - 1
 */
export function getLuminance(color: string): number {
    const decomposedColor = decomposeColor(color);

    let rgb = decomposedColor.type === 'hsl' ? decomposeColor(hslToRgb(color)).values : decomposedColor.values;
    rgb = rgb.map((val) => {
        let newVal: number = val as number;
        if (decomposedColor.type !== 'color') {
            newVal /= 255; // normalized
        }
        return newVal <= 0.03928 ? newVal / 12.92 : ((newVal + 0.055) / 1.055) ** 2.4;
    });

    // Truncate at 3 digits
    return Number((0.2126 * (rgb[0] as number) + 0.7152 * (rgb[1] as number) + 0.0722 * (rgb[2] as number)).toFixed(3));
}

/**
 * Darkens a color.
 * @param {string} color - CSS color, i.e. one of: #nnn, #nnnnnn, rgb(), rgba(), hsl(), hsla(), color()
 * @param {number} coefficient - multiplier in the range 0 - 1
 * @returns {string} A CSS color string. Hex input values are returned as rgb
 */
export function darken(color: string, coefficient: number): string {
    const decomposedColor = decomposeColor(color);
    const clampCoefficient = clamp(coefficient);
    const { type } = decomposedColor;

    if (type.indexOf('hsl') !== -1) {
        decomposedColor.values[2] = parseInt(decomposedColor.values[2].toString()) * (1 - clampCoefficient);
    } else if (type.indexOf('rgb') !== -1 || type.indexOf('color') !== -1) {
        for (let i = 0; i < 3; i += 1) {
            decomposedColor.values[i] = parseInt(decomposedColor.values[i].toString()) * (1 - clampCoefficient);
        }
    }

    return recomposeColor(decomposedColor);
}

/**
 * Lightens a color.
 * @param {string} color - CSS color, i.e. one of: #nnn, #nnnnnn, rgb(), rgba(), hsl(), hsla(), color()
 * @param {number} coefficient - multiplier in the range 0 - 1
 * @returns {string} A CSS color string. Hex input values are returned as rgb
 */
export function lighten(color: string, coefficient: number): string {
    const decomposedColor = decomposeColor(color);
    const clampCoefficient = clamp(coefficient);
    const { type } = decomposedColor;

    if (type.indexOf('hsl') !== -1) {
        const value = parseInt(decomposedColor.values[2].toString());
        decomposedColor.values[2] = value + (100 - value) * clampCoefficient;
    } else if (type.indexOf('rgb') !== -1) {
        for (let i = 0; i < 3; i += 1) {
            const value = parseInt(decomposedColor.values[i].toString());
            decomposedColor.values[i] = value + (255 - value) * clampCoefficient;
        }
    } else if (type.indexOf('color') !== -1) {
        for (let i = 0; i < 3; i += 1) {
            const value = parseInt(decomposedColor.values[i].toString());
            decomposedColor.values[i] = value + (1 - value) * clampCoefficient;
        }
    }

    return recomposeColor(decomposedColor);
}
